import { Middleware } from 'redux';

import type { StoreState } from '../state';
import { getStoreNumberFromStorage } from '../helpers/sessionStorage.helper';
import { ContextSources, deriveContext } from './context';
import { getPegxDocumentContext } from './pegxDocument';
import { tracker } from './tracker';

type TelemetryState = Pick<StoreState, 'router' | 'comparison' | 'solverOutput' | 'reviewAndEdit'>;

const readFallbackStoreNum = (): string => {
  try {
    return getStoreNumberFromStorage();
  } catch (e) {
    return '';
  }
};

export const readContextSources = (state: Partial<TelemetryState>): ContextSources => ({
  pathname: state.router?.location?.pathname || '/',
  search: state.router?.location?.search || '',
  versionForReview: state.comparison?.versionForReview || '',
  solverJobId: state.solverOutput?.job_id || '',
  fallbackStoreNum: readFallbackStoreNum(),
  pegx: getPegxDocumentContext(),
  editMode: state.reviewAndEdit?.editMode === true,
});

const inputsOf = (state: Partial<TelemetryState>): Array<unknown> => [
  state.router?.location?.pathname,
  state.router?.location?.search,
  state.comparison?.versionForReview,
  state.solverOutput?.job_id,
  state.reviewAndEdit?.editMode,
];

/**
 * Traffic is automatic: after every action the context (route + jobId) is re-derived and handed to the
 * tracker, which fires page_open when component | aisleCode | jobId | mode changed. Pages need no code.
 */
export const createTelemetryMiddleware = (): Middleware => (api) => {
  let lastInputs: Array<unknown> = [];

  tracker.setContextProvider(() => deriveContext(readContextSources(api.getState())));

  return (next) => (action) => {
    const result = next(action);

    try {
      const inputs = inputsOf(api.getState());

      if (inputs.some((value, index) => value !== lastInputs[index])) {
        lastInputs = inputs;
        tracker.refresh();
      }
    } catch (e) {
      // Telemetry must never break a dispatch.
    }

    return result;
  };
};
